import { XHRRequestCanceler } from '../../../../Libs/xhr/XHRRequestCanceler';
import { Constants } from '../../../Constants';
import {
    ControlsDataServiceName,
    IControlsDataService,
} from '../../../DataServices/ControlsDataService';
import { IMediaDataService, MediaDataServiceName } from '../../../DataServices/MediaDataService';
import { Lazy } from '../../../Helpers/Lazy';
import { ApiUrls } from '../../../Models/ApiUrls';
import { ControlListApiDataResponse } from '../../../Models/Control/Control';
import { DateUtils } from '../../../Utils/DateUtils';
import { ExportFileType } from '../../../Utils/ExportUtils';
import { IoC } from '../../ServicesContainer';
import { BaseReduxService } from '../Api/BaseReduxService';
import { I18nServiceName, II18nService } from '../I18n/I18nService';
import { IDownloadService, DownloadServiceName } from '../Download/DownloadService';
import { ControlListTodayViewType } from '../../../ViewModels/Controles/ControleListToday/ControleListTodayViewState';
import { BaseApiDataResponse } from '../../../Models/ApiData';

export interface IControlsService {
    getControls: (
        params: {
            templateId?: string;
            startDate?: Date;
            endDate?: Date;
            userId?: string;
            affaireId?: string;
        },
        cts: XHRRequestCanceler
    ) => Promise<ControlListApiDataResponse>;

    exportPdf: (checkId: string, pdfTitle: string, cts: XHRRequestCanceler) => void;

    getControlTodayList: (
        params: {
            compagnonId?: string;
            day_date?: Date;
            from_day_date?: Date;
            to_day_date?: Date;
        },
        cts: XHRRequestCanceler,
        state: ControlListTodayViewType
    ) => Promise<ControlListApiDataResponse>;

    regenerateControlToday: (cts: XHRRequestCanceler) => Promise<BaseApiDataResponse>;
}

class ControlsService extends BaseReduxService implements IControlsService {
    private readonly controlsDataService: Lazy<IControlsDataService>;
    private readonly i18nService: Lazy<II18nService>;
    private readonly mediaDataService: Lazy<IMediaDataService>;
    private readonly downloadService: Lazy<IDownloadService>;

    constructor() {
        super();
        this.controlsDataService = IoC.getLazy<IControlsDataService>(ControlsDataServiceName);
        this.i18nService = IoC.getLazy<II18nService>(I18nServiceName);
        this.mediaDataService = new Lazy<IMediaDataService>(() =>
            IoC.get<IMediaDataService>(MediaDataServiceName)
        );
        this.downloadService = new Lazy<IDownloadService>(() =>
            IoC.get<IDownloadService>(DownloadServiceName)
        );
    }

    public getControls = (
        params: {
            templateId?: string;
            startDate?: Date;
            endDate?: Date;
            userId?: string;
            affaireId?: string;
        },
        cts: XHRRequestCanceler
    ): Promise<ControlListApiDataResponse> => {
        try {
            const startDateAsString = params.startDate
                ? this.i18nService.value().formatMessage('Pointage_WeekDateFormat', {
                      year: params.startDate.getFullYear(),
                      month: DateUtils.formatTwoDigitsNumber(params.startDate.getMonth() + 1),
                      day: DateUtils.formatTwoDigitsNumber(params.startDate.getDate()),
                  })
                : undefined;

            const endDateAsString = params.endDate
                ? this.i18nService.value().formatMessage('Pointage_WeekDateFormat', {
                      year: params.endDate.getFullYear(),
                      month: DateUtils.formatTwoDigitsNumber(params.endDate.getMonth() + 1),
                      day: DateUtils.formatTwoDigitsNumber(params.endDate.getDate()),
                  })
                : undefined;

            const response = this.controlsDataService.value().getControlsList(
                {
                    check_template_id: params.templateId || undefined,
                    affaire_id: params.affaireId || undefined,
                    compagnon_id: params.userId || undefined,
                    from_day_date: startDateAsString,
                    to_day_date: endDateAsString,
                },
                cts
            );
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public exportPdf = async (checkId: string, pdfTitle: string, cts: XHRRequestCanceler) => {
        try {
            const url = Constants.getRoot() + ApiUrls.CHECK_EXPORT_PDF(checkId);
            this.downloadService.value().downloadFile(url, pdfTitle, ExportFileType.PDF, cts);
        } catch (err) {
            Promise.reject(err);
        }
    };

    public getControlTodayList = async (
        params: {
            compagnonId?: string;
            day_date?: Date;
            from_day_date?: Date;
            to_day_date?: Date;
        },
        cts: XHRRequestCanceler,
        state: ControlListTodayViewType
    ) => {
        try {
            const response = await this.controlsDataService.value().getControlTodayList(
                {
                    day_date: params.day_date ? this.i18nService.value().formatMessage('Control_DayDateFormat', {
                        day: DateUtils.formatTwoDigitsNumber(params.day_date.getDate()),
                        month: DateUtils.formatTwoDigitsNumber(params.day_date.getMonth() + 1),
                        year: params.day_date.getFullYear(),
                    }) : undefined,
                    from_day_date: params.from_day_date ? this.i18nService.value().formatMessage('Control_DayDateFormat', {
                        day: DateUtils.formatTwoDigitsNumber(params.from_day_date.getDate()),
                        month: DateUtils.formatTwoDigitsNumber(params.from_day_date.getMonth() + 1),
                        year: params.from_day_date.getFullYear(),
                    }) : undefined,
                    to_day_date: params.to_day_date ? this.i18nService.value().formatMessage('Control_DayDateFormat', {
                        day: DateUtils.formatTwoDigitsNumber(params.to_day_date.getDate()),
                        month: DateUtils.formatTwoDigitsNumber(params.to_day_date.getMonth() + 1),
                        year: params.to_day_date.getFullYear(),
                    }) : undefined,
                    compagnonId: params.compagnonId,
                },
                cts,
                state
            );
            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    public regenerateControlToday = async (cts: XHRRequestCanceler) => {
        try {
            const response = await this.controlsDataService.value().regenerateControlToday(cts);
            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    };
}

const ControlsServiceName = 'ControlsService';
export { ControlsService, ControlsServiceName };
